import React, { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { Pencil, Eye, UserCheck, UserX } from 'lucide-react'
import UserContext, { Roles } from '../../../../contexts/UserContext'
import axios from '../../../../config/axios'
import { useProfileContext } from '../../../../contexts/AccessesContext'
import ModalContext, { AccessUserForEdit } from '../../../../contexts/ModalContext'
import { parseUserStatus, UserStatus } from '../../../../types/userStatus'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { PeopleOutletType } from '..'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { accessesScreenKey, buildAccessesColumns, AccessUserRow } from './accessesColumns'
import {
  activateAccessUser,
  deactivateAccessUser,
} from '../../../../components/EditAccessUserModal'
import ConfirmationModal from '../../../../components/ModalConfirmation'

export const toAccessUserForEdit = (row: AccessUserRow): AccessUserForEdit => ({
  ...row,
  id: row.id,
  name: row.name ?? '',
  email: row.email ?? '',
  phone: row.phone ?? '',
  company: row.company ?? '',
  picture: (row as any).picture || undefined,
  status: parseUserStatus(row.status),
  cnpj: row.cnpj ?? '',
  adminUser: !!row.adminUser,
  permissions: Array.isArray(row.permissions) ? row.permissions : [],
  approved: row.approved ?? '',
  attachments: ((row as any).FileInUser ?? (row as any).attachments ?? []).map((f: any) => ({
    id: f.GeneralFile?.id || f.fileId || f.id,
    fileUrl: f.GeneralFile?.fileUrl || f.fileUrl,
    title: f.GeneralFile?.title || f.title,
    documentType: f.documentType || 'GERAL',
    expirationDate: f.expirationDate || null,
    status: f.status || null,
  })),
  skills: (row.UserSkill ?? []).map((us) => ({ id: us.Skill.id, name: us.Skill.name })),
})

const TableAccesses = () => {
  const { user } = useContext(UserContext)
  const {
    setModalActive,
    setModalSection,
    setSelectedAccessUser,
    setRefreshCallback,
  } = useContext(ModalContext)
  const [users, setUsers] = useState<AccessUserRow[]>([])
  const [updateUserData, setUpdateUserData] = useState(0)
  const { usersLoadQuantity, setUsersLoadQuantity, fetchProfilesWithoutAdmin, profiles } = useProfileContext()
  const { search, appliedFilters, refresh, setLoadingHeader } = useOutletContext<PeopleOutletType>()
  const [hasMoreUsers, setHasMoreUsers] = useState(true)
  const [totalUsers, setTotalUsers] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [statusConfirmTarget, setStatusConfirmTarget] = useState<{
    row: AccessUserRow
    action: 'activate' | 'deactivate'
  } | null>(null)

  const [searchParams] = useSearchParams()
  const userIdParam = searchParams.get('userId') || searchParams.get('id')
  const openedUserIdRef = useRef<number | null>(null)
  const isTenantAdmin = user?.role === Roles.tenantAdmin

  useEffect(() => {
    if (!userIdParam) return
    const parsedId = Number(userIdParam)
    if (isNaN(parsedId) || parsedId <= 0 || openedUserIdRef.current === parsedId) return

    openedUserIdRef.current = parsedId
    const existing = users.find((u) => u.id === parsedId)
    if (existing) {
      if (isTenantAdmin) {
        openEditAccessUser(existing)
      } else {
        openViewAccessUser(existing)
      }
      return
    }

    ApiService.user.findById(parsedId)
      .then((response) => {
        const found = response.data?.data || response.data
        if (found) {
          if (isTenantAdmin) {
            openEditAccessUser(found)
          } else {
            openViewAccessUser(found)
          }
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [userIdParam, isTenantAdmin, users])

  const handleConfirmStatusChange = async () => {
    if (!statusConfirmTarget) return
    try {
      if (statusConfirmTarget.action === 'deactivate') {
        await deactivateAccessUser(statusConfirmTarget.row.id)
      } else {
        await activateAccessUser(statusConfirmTarget.row.id)
      }
      setStatusConfirmTarget(null)
      setUpdateUserData((p) => p + 1)
    } catch (e) {
      console.error('[AccessesTable] Falha ao alterar status do usuário', e)
    }
  }

  const mappedFilters = useMemo(
    () =>
      appliedFilters.map((f) => ({
        field: f.label,
        type: f.filterType,
        text: String(f.value),
      })),
    [appliedFilters],
  )

  const openViewAccessUser = (row: AccessUserRow) => {
    setSelectedAccessUser(toAccessUserForEdit(row))
    setModalSection('viewAccessUser')
    setModalActive(true)
  }

  const openEditAccessUser = (row: AccessUserRow) => {
    setRefreshCallback(() => () => setUpdateUserData((p) => p + 1))
    setSelectedAccessUser(toAccessUserForEdit(row))
    setModalSection('editAccessUser')
    setModalActive(true)
  }

  const fetchUsers = () => {
    setIsLoading(true)
    setLoadingHeader?.(true)
    axios
      .get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`, {
        params: {
          search,
          loadQuantity: usersLoadQuantity,
          filters: JSON.stringify(mappedFilters),
          excludeAdminProfile: 'true',
        }
      })
      .then((response) => {
        const resp = response.data
        const usersData = Array.isArray(resp) ? resp : resp.data || []
        const total = typeof resp === 'object' && typeof resp.total === 'number' ? resp.total : undefined

        let hasMoreData = false
        let dataToShow: AccessUserRow[] = usersData

        if (typeof total === 'number') {
          hasMoreData = usersData.length < total
          dataToShow = usersData
        } else {
          const legacyHasMore = usersData.length > usersLoadQuantity
          dataToShow = legacyHasMore ? usersData.slice(0, usersLoadQuantity) : usersData
          hasMoreData = legacyHasMore
        }

        setUsers(dataToShow)
        setHasMoreUsers(hasMoreData)
        setTotalUsers(typeof total === 'number' ? total : null)
      })
      .catch((error) => {
        console.error('Ocorreu um erro ao obter os dados dos users', error)
        setHasMoreUsers(false)
      })
      .finally(() => {
        setIsLoading(false)
        setLoadingHeader?.(false)
      })
    fetchProfilesWithoutAdmin()
  }

  const isFirstRenderForUsers = useRef(true)
  useEffect(() => {
    if (isFirstRenderForUsers.current) {
      isFirstRenderForUsers.current = false
      return
    }
    if (refresh) fetchUsers()
  }, [refresh])

  useEffect(() => {
    fetchUsers()
  }, [updateUserData, search, usersLoadQuantity, mappedFilters])

  const columns = useMemo(() => buildAccessesColumns(profiles), [profiles])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    accessesScreenKey,
    columns,
  )

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={users}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalUsers}
        onLoadMore={() => setUsersLoadQuantity(usersLoadQuantity + 50)}
        hasMore={hasMoreUsers}
        isLoading={isLoading}
        countLabel="usuários"
        exportFileName="usuarios"
        rowClassName={(row) => (parseUserStatus(row.status) === UserStatus.INACTIVE ? 'inactiveRow' : '')}
        rowActions={(row) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => openViewAccessUser(row) },
          ...(isTenantAdmin
            ? [
                { label: 'Editar', icon: <Pencil size={16} />, onClick: () => openEditAccessUser(row) },
                parseUserStatus(row.status) === UserStatus.INACTIVE
                  ? {
                      label: 'Ativar usuário',
                      icon: <UserCheck size={16} />,
                      onClick: () => setStatusConfirmTarget({ row, action: 'activate' }),
                    }
                  : {
                      label: 'Desativar usuário',
                      icon: <UserX size={16} />,
                      onClick: () => setStatusConfirmTarget({ row, action: 'deactivate' }),
                    },
              ]
            : []),
        ]}
        emptyMessage="Nenhum usuário encontrado"
      />

      <ConfirmationModal
        isOpen={statusConfirmTarget !== null}
        title={statusConfirmTarget?.action === 'activate' ? 'Ativar usuário' : 'Desativar usuário'}
        message={
          statusConfirmTarget?.action === 'activate' ? (
            <p style={{ margin: 0 }}>
              O usuário voltará a ter acesso conforme o perfil de acesso definido.
            </p>
          ) : (
            <p style={{ margin: 0 }}>
              Isso poderá ser revertido, mas o usuário não terá acesso até então.
            </p>
          )
        }
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setStatusConfirmTarget(null),
            variant: 'outline',
          },
          statusConfirmTarget?.action === 'activate'
            ? {
                label: 'Ativar',
                onClick: handleConfirmStatusChange,
                variant: 'primary' as const,
              }
            : {
                label: 'Desativar',
                onClick: handleConfirmStatusChange,
                variant: 'danger' as const,
              },
        ]}
      />
    </>
  )
}

export default TableAccesses
